import React from "react";
import { useTranslation } from "react-i18next";

import AppDatePicker from "../../Core/AppDatePicker/AppDatePicker";
import Dropdown from "../../Core/Dropdown/Dropdown";
import {
	buildCertificationKeyValues,
	certificationStatusTypes,
} from "../../../configs/Reports.config";
import { KeyValue } from "../../../models/KeyValue";
import { CertificationType } from "../../../models/Organisation";

export interface CertificationFilterValue {
	/** Org defined certification type key. */
	typeKey?: string;
	/** One of valid | expired | missing. */
	status?: string;
	/** Only include certifications expiring on or before this date. */
	expiresBy?: Date;
}

interface CertificationReportFilterProps {
	certificationTypes?: CertificationType[];
	value: CertificationFilterValue;
	onChange: (value: CertificationFilterValue) => void;
	allLabel: string;
	/** Matches the margin the surrounding report page gives its other filters. */
	margin?: string;
}

/**
 * Certification filter shared by the consultant and candidate report pages.
 *
 * Replaces the two hard coded "Giltigt utdrag ur belastningsregistret" and
 * "Körkort" dropdowns, which pointed at certification types that no longer
 * exist under those keys.
 *
 * Status and "expires by" only mean something for a specific certification
 * type, so both are disabled until one is picked — and clearing the type
 * clears them again. Organisations without a certification taxonomy get no
 * filter at all.
 */
const CertificationReportFilter: React.FC<CertificationReportFilterProps> = ({
	certificationTypes,
	value,
	onChange,
	allLabel,
	margin = "0.75rem 0.5rem",
}) => {
	const { t } = useTranslation();

	if (!certificationTypes?.length) return null;

	const typeOptions = buildCertificationKeyValues(certificationTypes, allLabel);
	const statusOptions = certificationStatusTypes();

	const selectedType = typeOptions.find((option) => option.key === (value.typeKey || ""));
	const selectedStatus = statusOptions.find((option) => option.key === (value.status || ""));

	const handleTypeChange = (option: KeyValue) => {
		const typeKey = (option.key as string) || undefined;

		// Without a type there is nothing for the other two to qualify
		onChange(typeKey ? { ...value, typeKey } : {});
	};

	const handleStatusChange = (option: KeyValue) =>
		onChange({ ...value, status: (option.key as string) || undefined });

	const handleExpiresByChange = (expiresBy: Date) => onChange({ ...value, expiresBy });

	return (
		<>
			<Dropdown<KeyValue, KeyValue>
				value={selectedType}
				getValue={(o) => o}
				onChange={handleTypeChange}
				getOptionLabel={(option) => option.value}
				options={typeOptions}
				placeholder={"reports.filters.certification.placeHolder"}
				label={"reports.filters.certification.label"}
				width="300px"
				margin={margin}
			/>
			<Dropdown<KeyValue, KeyValue>
				value={selectedStatus}
				getValue={(o) => o}
				onChange={handleStatusChange}
				getOptionLabel={(option) => option.value}
				options={statusOptions}
				disabled={!value.typeKey}
				placeholder={"reports.filters.certificationStatus.placeHolder"}
				label={"reports.filters.certificationStatus.label"}
				width="300px"
				margin={margin}
			/>
			<AppDatePicker
				label="reports.filters.certificationExpiresBy.label"
				placeholderText={t("reports.filters.certificationExpiresBy.placeHolder")}
				inputStyles={true}
				inputWidth="300px"
				disabled={!value.typeKey}
				selectedDate={value.expiresBy}
				onChange={handleExpiresByChange}
				margin={margin}
			/>
		</>
	);
};

export default CertificationReportFilter;
